// Which documents belong to the vendor's Documents tab, and what they are
// called there. Lives in @l2u/shared because the API (the Notify email decides
// which items are documents) and both clients must agree on the set, and the
// API is a Worker that cannot import @l2u/ui.

export type DocumentsTabQuestion = { include_in_compliance_documents: boolean };
/** `status` is a plain string so both the API's submissions rows and the client's SubmissionStatus satisfy it. */
export type DocumentsTabSubmission = { status: string; reverify_requested_at?: string | null };

/**
 * THE Documents-tab set: compliance-flagged AND (approved OR in a renewal cycle).
 * Same rule as the vendor Documents page (was apps/vendor isApprovedComplianceDoc).
 *
 * A compliance document is one whose question is flagged to appear in
 * Compliance Documents (include_in_compliance_documents, migration 0031) AND
 * whose submission has been approved by a reviewer — i.e. marked compliant.
 * Until approval the document stays in the questionnaire view, not the
 * compliance-documents view. The vendor Compliance Documents page uses it; the
 * admin Compliance Documents tab expresses the same rule in its own code, via an
 * approvedIds set.
 */
export function isDocumentsTabDocument(
  question: DocumentsTabQuestion | undefined | null,
  submission: DocumentsTabSubmission | undefined | null,
): boolean {
  if (question?.include_in_compliance_documents !== true) return false;
  // Approved compliance docs live in the Compliance Documents tab. A doc in a
  // renewal cycle — previously approved, the vendor has uploaded a newer version
  // now awaiting re-review (reverify_requested_at set, status back to pending) —
  // stays pinned here so it never vanishes from the vendor's compliance view
  // mid-renewal. Cleared only when the renewal is itself approved/N-A.
  return submission?.status === 'approved' || submission?.reverify_requested_at != null;
}

/**
 * Display name for a document row in the Compliance Documents views (admin
 * tab + vendor page): the admin-configured Compliance Document Label when
 * set, else the question title. Blank/whitespace labels count as unset.
 */
export function complianceDocName(q: {
  label: string;
  compliance_doc_label: string | null;
}): string {
  const custom = q.compliance_doc_label?.trim();
  return custom ? custom : q.label;
}
